import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, X, Users, Calendar, GraduationCap, CreditCard } from "lucide-react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";

interface SearchResult {
  id: string;
  name: string;
  type: "families" | "students" | "classes" | "staff";
  details?: string;
}

const mockData: SearchResult[] = [
  // Students starting with S
  { id: "1", name: "Sarah Johnson", type: "students", details: "Age 12, Ballet & Jazz" },
  { id: "2", name: "Sarah Miller", type: "students", details: "Age 10, Contemporary" },
  { id: "3", name: "Sam Rodriguez", type: "students", details: "Age 14, Hip Hop" },
  { id: "4", name: "Samantha Chen", type: "students", details: "Age 8, Ballet" },
  { id: "5", name: "Sophie Williams", type: "students", details: "Age 11, Jazz" },
  { id: "6", name: "Steven Park", type: "students", details: "Age 13, Break Dancing" },
  
  // Families
  { id: "7", name: "Johnson Family", type: "families", details: "2 students, Current payments" },
  { id: "8", name: "Smith Family", type: "families", details: "1 student, Outstanding balance" },
  { id: "9", name: "Sanders Family", type: "families", details: "3 students, Active" },
  
  // Classes
  { id: "10", name: "Soccer Fundamentals", type: "classes", details: "Tuesdays 4-5 PM" },
  { id: "11", name: "Senior Ballet", type: "classes", details: "Saturdays 10-11 AM" },
  { id: "12", name: "Swimming Lessons", type: "classes", details: "Mondays 3-4 PM" },
  
  // Staff
  { id: "13", name: "Sarah Thompson (Staff)", type: "staff", details: "Ballet Instructor" },
  { id: "14", name: "Steve Miller (Staff)", type: "staff", details: "Soccer Coach" },
];

const ConnectedDataCard: React.FC<{ student: SearchResult }> = ({ student }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto"
    >
      {/* Student Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl p-6 shadow-lg border border-blue-100"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Student</h3>
            <p className="text-sm text-gray-600">Profile</p>
          </div>
        </div>
        <div className="space-y-2">
          <p className="font-medium">{student.name}</p>
          <p className="text-sm text-gray-600">Age 12</p>
          <p className="text-sm text-gray-600">Active since 2022</p>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-green-600">Active</span>
          </div>
        </div>
      </motion.div>

      {/* Family Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl p-6 shadow-lg border border-purple-100"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
            <Users className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Family</h3>
            <p className="text-sm text-gray-600">Connection</p>
          </div>
        </div>
        <div className="space-y-2">
          <p className="font-medium">Johnson Family</p>
          <p className="text-sm text-gray-600">Parent: Mike Johnson</p>
          <p className="text-sm text-gray-600">Phone: (555) 123-4567</p>
          <p className="text-sm text-gray-600">2 children enrolled</p>
        </div>
      </motion.div>

      {/* Classes Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl p-6 shadow-lg border border-green-100"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Calendar className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Classes</h3>
            <p className="text-sm text-gray-600">Enrolled</p>
          </div>
        </div>
        <div className="space-y-2">
          <p className="font-medium">Ballet Beginner</p>
          <p className="text-sm text-gray-600">Tuesdays 4:00 PM</p>
          <p className="font-medium">Jazz Intermediate</p>
          <p className="text-sm text-gray-600">Thursdays 5:00 PM</p>
        </div>
      </motion.div>

      {/* Payment Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl p-6 shadow-lg border border-orange-100"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Payments</h3>
            <p className="text-sm text-gray-600">Status</p>
          </div>
        </div>
        <div className="space-y-2">
          <p className="font-medium text-green-600">Current</p>
          <p className="text-sm text-gray-600">$150/month</p>
          <p className="text-sm text-gray-600">Last payment: Dec 1</p>
          <p className="text-sm text-gray-600">Next due: Jan 1</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

const SearchDemo: React.FC = () => {
  const [query, setQuery] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<SearchResult | null>(null);
  const [typingStep, setTypingStep] = useState(0);

  const searchSequence = ["", "S", "Sa", "Sar", "Sara", "Sarah"];

  useEffect(() => {
    const startDemo = () => {
      setIsTyping(true);
      setQuery("");
      setShowResults(false);
      setSelectedStudent(null);
      setTypingStep(0);

      const typeSequence = async () => {
        for (let i = 0; i < searchSequence.length; i++) {
          await new Promise(resolve => setTimeout(resolve, i === 0 ? 1000 : 800));
          setQuery(searchSequence[i]);
          setTypingStep(i);
          if (i > 0) {
            setShowResults(true);
          }
        }
        
        // Pause at full "Sarah" results
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Select Sarah Johnson
        const sarah = mockData.find(item => item.name === "Sarah Johnson");
        if (sarah) {
          setSelectedStudent(sarah);
        }
        
        // Wait before restarting
        await new Promise(resolve => setTimeout(resolve, 4000));
        setIsTyping(false);
        
        // Restart after a pause
        setTimeout(startDemo, 3000);
      };

      typeSequence();
    };

    startDemo();
  }, []);

  const getFilteredResults = () => {
    if (!query) return { families: [], students: [], classes: [], staff: [] };

    const filtered = mockData.filter(item => 
      item.name.toLowerCase().includes(query.toLowerCase())
    );

    return {
      families: filtered.filter(item => item.type === "families"),
      students: filtered.filter(item => item.type === "students"),
      classes: filtered.filter(item => item.type === "classes"),
      staff: filtered.filter(item => item.type === "staff"),
    };
  };

  const results = getFilteredResults();
  const hasResults = Object.values(results).some(arr => arr.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Hivetivity Global Search Demo
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Watch how you can find any student, family, class, or staff member instantly. 
            No more clicking through menus - just type and see everything connected.
          </p>
        </div>

        {/* Search Demo Container */}
        <div className="max-w-4xl mx-auto">
          {/* Search Bar */}
          <div className="relative mb-8">
            <div className="flex h-16 w-full items-center rounded-2xl border-2 border-primary/60 bg-white px-6 py-4 shadow-lg hover:shadow-xl transition-all duration-200">
              <Zap className="mr-4 h-6 w-6 shrink-0 text-yellow-500 drop-shadow-[0_0_6px_rgba(251,191,36,0.6)]" />
              <div className="flex-1 relative">
                <input
                  className="w-full text-lg font-medium bg-transparent outline-none"
                  value={query}
                  placeholder="Search families, students, staff..."
                  readOnly
                />
                {isTyping && (
                  <motion.div
                    className="absolute right-0 top-0 h-full w-0.5 bg-gray-900"
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  />
                )}
              </div>
              {query && (
                <X className="h-5 w-5 shrink-0 opacity-50 cursor-pointer ml-2" />
              )}
            </div>
            
            {/* AI Badge */}
            <div className="flex justify-end mt-2">
              <span className="text-sm text-yellow-600 bg-yellow-100 rounded-full px-3 py-1 font-semibold shadow-sm">
                ⚡ Powered by AI
              </span>
            </div>

            {/* Search Results Dropdown */}
            <AnimatePresence>
              {showResults && hasResults && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-md shadow-2xl rounded-2xl border-0 overflow-hidden z-50"
                >
                  <Command>
                    <CommandList className="max-h-96 overflow-y-auto p-4">
                      {results.families.length > 0 && (
                        <CommandGroup heading="Families">
                          {results.families.map((family) => (
                            <CommandItem
                              key={family.id}
                              className="px-4 py-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <div>
                                <div className="font-medium">{family.name}</div>
                                <div className="text-sm text-gray-600">{family.details}</div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}

                      {results.students.length > 0 && (
                        <>
                          {results.families.length > 0 && <CommandSeparator className="my-2" />}
                          <CommandGroup heading="Students">
                            {results.students.map((student) => (
                              <CommandItem
                                key={student.id}
                                className="px-4 py-3 rounded-lg hover:bg-blue-50 cursor-pointer"
                                onSelect={() => {
                                  if (student.name === "Sarah Johnson") {
                                    setSelectedStudent(student);
                                  }
                                }}
                              >
                                <div>
                                  <div className="font-medium">{student.name}</div>
                                  <div className="text-sm text-gray-600">{student.details}</div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}

                      {results.classes.length > 0 && (
                        <>
                          {(results.families.length > 0 || results.students.length > 0) && <CommandSeparator className="my-2" />}
                          <CommandGroup heading="Classes">
                            {results.classes.map((classItem) => (
                              <CommandItem
                                key={classItem.id}
                                className="px-4 py-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                              >
                                <div>
                                  <div className="font-medium">{classItem.name}</div>
                                  <div className="text-sm text-gray-600">{classItem.details}</div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}

                      {results.staff.length > 0 && (
                        <>
                          {(results.families.length > 0 || results.students.length > 0 || results.classes.length > 0) && (
                            <CommandSeparator className="my-2" />
                          )}
                          <CommandGroup heading="Staff">
                            {results.staff.map((staff) => (
                              <CommandItem
                                key={staff.id}
                                className="px-4 py-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                              >
                                <div>
                                  <div className="font-medium">{staff.name}</div>
                                  <div className="text-sm text-gray-600">{staff.details}</div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Connected Data Display */}
          <AnimatePresence>
            {selectedStudent && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="mb-8"
              >
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Everything Connected to Sarah Johnson
                  </h2>
                  <p className="text-gray-600">
                    See how all data is instantly accessible and connected
                  </p>
                </div>
                <ConnectedDataCard student={selectedStudent} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Benefits */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Why This Matters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h4 className="font-semibold text-gray-900 mb-2">Save Time</h4>
                <p className="text-gray-600 text-sm">
                  No more clicking through 5 different sections to find what you need
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h4 className="font-semibold text-gray-900 mb-2">See Connections</h4>
                <p className="text-gray-600 text-sm">
                  Everything is linked - student, family, payments, and classes
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h4 className="font-semibold text-gray-900 mb-2">Work Smarter</h4>
                <p className="text-gray-600 text-sm">
                  One search gives you the full picture, not just fragments
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchDemo;